import React, { FC, useEffect, useState } from 'react'

import {
  ArrowRightOnRectangleIcon,
  CalculatorIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  TicketIcon,
} from '@heroicons/react/24/outline'
import LogRocket from 'logrocket'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { ClosingIcon } from '@/assets/icons/ClosingIcon'
import { DocVaultIcon } from '@/assets/icons/DocVaultIcon'
import { setUsername } from '@/client/generalSlice'
import { useAppDispatch } from '@/client/hooks'
import { NavSidebar } from '@/components/Nav/NavSidebar'
import { KickBackModal } from '@/features/DealKickBack'
import { DealSummaryBar } from '@/features/DealSummaryBar/DealSummaryBar'
import { AppraisalOrderModal } from '@/features/SidePanel/AppraisalOrderModal.deprecated'
import { ISidePanel, SidePanel } from '@/features/SidePanel/SidePanel'
import { TitleOrderModal } from '@/features/SidePanel/TitleOrderModal'
import { Application } from '@/graphql/generated'
import { useGetApplicationCalcsQuery } from '@/graphql/services/applicationCalcs'
import { useGetLoggedUserQuery } from '@/services/user'
import { getApplicationProfileByPosition } from '@/utils/applicationProfile'
import { easterEggPrivateRoute } from '@/utils/easterEgg'
import { splitStoreInit } from '@/utils/features/splitSdk'

interface IPrivateRouteLayout {
  applicationId?: string | string[] | number
  hideNavigation: boolean
  hideSidePanel?: boolean
  applicationData?: Application
  children?: React.ReactNode
  classNameMain?: string
  sidePanelOptions?: ISidePanel
}

const PrivateRouteLayout: FC<IPrivateRouteLayout> = ({
  applicationId,
  children,
  hideNavigation,
  hideSidePanel,
  applicationData,
  classNameMain = '',
  sidePanelOptions = {},
}) => {
  const dispatch = useAppDispatch()
  const { data: user, error } = useGetLoggedUserQuery()
  const logRocket = process.env.NEXT_PUBLIC_OPS_APP_LOG_ROCKET_PROJECT
  const router = useRouter()

  const { data: applicationCalcs } = useGetApplicationCalcsQuery(
    { externalId: applicationData?.externalId },
    {
      skip: !applicationData?.externalId,
    }
  )

  const applicationWithCalcs = { ...applicationData, ...applicationCalcs?.application }

  useEffect(() => {
    // Init Feature flags Store
    splitStoreInit()
    // Store username
    dispatch(setUsername(localStorage.getItem('username')))
  }, [dispatch])

  useEffect(() => {
    if (logRocket) {
      if (user) {
        LogRocket.identify(String(user?.id), {
          name: user?.username,
          email: user?.email,
        })
      }
    }
  }, [logRocket, user, error])

  useEffect(() => {
    if (error && 'status' in error) {
      const data = error.data as { detail: string }
      if (!user && error?.status === 403 && data.detail === 'INVALID_OTP') {
        router.push('/verification')
      }
    }
  }, [user, error])

  const applicantProfile = getApplicationProfileByPosition(applicationData?.uuid, 1)
  const propertyAddress = applicationData?.property?.address

  const iconsWidth = 18

  const [easterEggTag, setEasterEggTag] = useState(<></>)

  const navItems = [
    {
      path: `/applications/${applicationId}/summary`,
      label: 'Summary',
      enabled: true,
      hidden: false,
      icon: <TicketIcon width={iconsWidth} />,
      dataTest: 'deal-summary-nav-button',
    },
    {
      path: `/applications/${applicationId}`,
      label: 'Application',
      enabled: true,
      hidden: false,
      icon: <DocumentDuplicateIcon width={iconsWidth} />,
      dataTest: 'application-nav-button',
    },
    {
      path: `/applications/${applicationId}/avm`,
      label: 'AVM',
      enabled: true,
      hidden: false,
      icon: <CalculatorIcon width={iconsWidth} />,
      dataTest: 'avm-nav-button',
    },
    {
      path: `/applications/${applicationId}/underwriting`,
      label: 'Underwriting',
      enabled: true,
      hidden: false,
      icon: <DocumentTextIcon width={iconsWidth} />,
      dataTest: 'underwriting-nav-button',
    },
    {
      path: `/applications/${applicationId}/documents`,
      label: 'Documents',
      enabled: true,
      hidden: false,
      icon: <DocVaultIcon width={iconsWidth} />,
      dataTest: 'file-manager-nav-button',
    },
    {
      path: `/applications/${applicationId}/closing`,
      label: 'Closing',
      enabled: true,
      hidden: false,
      icon: <ClosingIcon width={iconsWidth} />,
      dataTest: 'closing-nav-button',
    },
    {
      path: '/logout',
      label: 'Logout',
      enabled: true,
      hidden: false,
      icon: <ArrowRightOnRectangleIcon width={iconsWidth} />,
      dataTest: 'logout-nav-button',
    },
    {
      path: '',
      label: 'Joe Mama',
      enabled: false,
      hidden: false,
      icon: (
        <div
          className='p-2'
          onClick={() => {
            easterEggPrivateRoute(setEasterEggTag)
          }}
        ></div>
      ),
      dataTest: 'easter-egg',
    },
  ]

  return (
    <>
      <Head>
        <title>
          {applicationData?.name
            ? applicationData?.name
            : applicantProfile?.profile?.last_name && propertyAddress
              ? `${applicantProfile?.profile?.last_name} - ${propertyAddress?.street}, ${propertyAddress?.city}, ${propertyAddress?.state}, ${propertyAddress?.zipCode}`
              : process.env.NODE_ENV === 'development'
                ? '* Unlock Ops * DEV'
                : 'Unlock Ops'}
        </title>
        <link
          rel='icon'
          href='/favicon.png'
        />
      </Head>
      <div className='flex'>
        <TitleOrderModal />
        <AppraisalOrderModal />

        <div className='grow'>
          {applicationData && <DealSummaryBar application={applicationWithCalcs} />}
          <div className='flex min-h-[calc(100%_-_200px)]'>
            <KickBackModal />
            {!hideNavigation && <NavSidebar items={navItems} />}
            <main className={`flex-grow ${classNameMain}`}>{children}</main>
          </div>
        </div>
        {!hideSidePanel && <SidePanel {...sidePanelOptions} />}
      </div>
      {easterEggTag}
    </>
  )
}
export default PrivateRouteLayout
